<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useChatStore } from '#layers/ai/stores/chat';
import AiChatThreadPromptSpinner from './Spinner.vue';
import AiChatThreadPromptSubmit from './Submit.vue';

const chatStore = useChatStore();
const { t } = useI18n();

const inputValue = ref('');
const trimmedInput = computed(() => inputValue.value.trim());
const isApprovalBlocked = computed(() => !chatStore.canSendMessage && !chatStore.isBusy);
const canSubmit = computed(() => trimmedInput.value.length > 0 && chatStore.canSendMessage);

function sendMessage() {
	if (!canSubmit.value) return;

	chatStore.sendMessage(trimmedInput.value);
	inputValue.value = '';
}
</script>

<template>
	<div class="relative w-full">
		<Transition
			enter-active-class="transition-all duration-200 ease-out"
			enter-from-class="translate-y-[5px] opacity-0"
			enter-to-class="translate-y-0 opacity-100"
			leave-active-class="transition-all duration-200 ease-in"
			leave-from-class="translate-y-0 opacity-100"
			leave-to-class="translate-y-[5px] opacity-0"
		>
			<AiChatThreadPromptSpinner
				v-if="chatStore.isBusy"
				class="pointer-events-none absolute inset-x-0 bottom-full bg-default py-1 pb-2"
				:salt="chatStore.contentBlockCount"
			/>
		</Transition>

		<form
			class="
				relative w-full rounded-md bg-default ring ring-default transition-shadow ring-inset
				focus-within:ring-2 focus-within:ring-primary focus-within:ring-inset
				hover:duration-0
				focus-within:hover:ring-primary
				interactable-hover:ring-accented
			"
			@submit.prevent="sendMessage"
		>
			<DTextarea
				v-model="inputValue"
				data-testid="ai-chat-prompt-input"
				:placeholder="t('ai.chat.prompt_placeholder')"
				variant="none"
				autoresize
				:rows="1"
				:maxrows="8"
				block
				:disabled="isApprovalBlocked"
				:ui="{
					base: 'pb-13',
				}"
				@keydown.enter.exact.prevent="sendMessage"
			/>

			<AiChatThreadPromptSubmit class="absolute inset-e-2 bottom-2" />
		</form>
	</div>
</template>
